import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { Observable, combineLatest, from, iif, merge, of, timer, zip } from 'rxjs';
import {
  catchError,
  concatMap,
  delay,
  filter,
  map,
  mergeMap,
  repeatWhen,
  retry,
  switchMap,
  take,
  takeUntil,
  tap,
  timeout,
  toArray,
} from 'rxjs/operators';

import {
  ArrayValue,
  ClientService,
  DeviceHeatingCircuitIdCommands,
  DeviceIds,
  DeviceRoles,
  Feature,
  FeatureActionFields,
  FeatureCommands,
  FeatureDeprecated,
  FeatureNames,
  FeaturePropertyKey,
  IsEnabledProperty,
  IsNumberValueProperty,
  OperationalDataApi,
  ZigbeeCoordinatorCommands,
} from '@vi-iot/client';
import { LogType, ViLogger } from '@vi-iot/ui';

import { UIService } from '@services';
import {
  CommissioningActions,
  GatewayStore,
  Helpers,
  InstallationDetailsActions,
  InstallationFeaturesActions,
  InstallationFeaturesStore,
} from '@store';

import { TranslateService } from '@ngx-translate/core';
import { CommissioningMessageService } from '../_services/commissioning-message.service';
import { UpdateMessageService } from '../_services/update-message.service';
import { AppState } from '../app.state';
import { GatewayDevice } from '../gateway/gateway.model';
import { InstallationDetailsStore } from '../installation-details/installation-details.store';

import {
  AddDeviceType,
  COMMISSIONING_STEP,
  DEVICE_STATUS,
  DeviceZigbeeCoordinatorProperties,
  PAIRING_STEPS,
  RemoteDeviceType,
  ValidateEuidResult,
  ZIGBEE_COORDINATOR_STATUS,
} from './commissioning.model';
import { CommissioningStore } from './commissioning.store';
import {
  CommissioningDeviceAssignChannelParams,
  CommissioningDeviceAssignHeatingCircuitParams,
  CommissioningDeviceAssignRoomParams,
  CommissioningDeviceAssignValidationParams,
  CommissioningRemoteDeviceAssignHeatingCircuitParams,
  CommissioningRemoteDeviceAssignVentilationParams,
} from './commissioning.types';

@Injectable()
export class CommissioningEffects {
  resetFeatures$: Observable<Action> = createEffect(
    () => {
      return this.actions$.pipe(ofType(CommissioningActions.resetFeatures));
    },
    { dispatch: false }
  );

  listCommissioningFeatures$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(CommissioningActions.listFeatures),
      mergeMap((action) =>
        combineLatest([
          of(action),
          this.installationDetailsStore.getInstallationId$.pipe(take(1)),
          this.installationDetailsStore.gatewaySerial$.pipe(take(1)),
        ])
      ),
      switchMap(([action, installationId, gatewaySerial]) =>
        combineLatest([
          of(action),
          of(installationId),
          of(gatewaySerial),
          this.api
            .getGatewayFeature(installationId, gatewaySerial, FeatureNames.gatewayDevices)
            .pipe(map(extractDevices), map(getCoordinatorDeviceId)),
        ])
      ),
      switchMap(([_action, installationId, gatewaySerial, coordinatorDeviceId]) =>
        this.api
          .getDeviceFeature(
            installationId,
            gatewaySerial,
            coordinatorDeviceId,
            FeatureNames.deviceZigbeeCoordinator
          )
          .pipe(
            map((payload) =>
              CommissioningActions.listFeaturesSuccess({
                feature: { [payload.data.feature]: payload.data },
                installationId,
                gatewaySerial,
              })
            )
          )
      ),
      catchError(() => of(CommissioningActions.listFeaturesError()))
    );
  });

  updateRemoteDeviceFeatureProperty$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(CommissioningActions.updateFeatureProperty),
      concatLatestFrom(() => [
        this.store.select('commissioning'),
        this.commissioningStore.isAddingDirectlyToRoom$,
      ]),
      map(([action, commissioningState, isAddingDirectlyToRoom]) => {
        return {
          ...action,
          feature: {
            ...commissioningState.feature[action.featureName],
          },
          isAddingDirectlyToRoom,
        };
      }),
      map((action) => ({
        action: Helpers.mapToPayload(action),
        isAddingDirectlyToRoom: action.isAddingDirectlyToRoom,
      })),
      tap(() => this.uiService.blockUI()),
      switchMap(({ action, isAddingDirectlyToRoom }) =>
        this.client.rawPost(action.uri, action.payload).pipe(
          tap(() => this.uiService.unblockUI()),
          tap(() => {
            if (action.config && action.config.hideSuccessSnackbar) {
              return;
            }
            this.updateMessageService.success(
              action.config.successMessage,
              action.featureName,
              action.commandName
            );
          }),
          concatMap(() =>
            // If user tries to add a device directly to a room verify if the device is assigned anywhere already.
            // If user tries to add a device generally verify if it is already added.
            iif(
              () => isAddingDirectlyToRoom,
              this.installationFeaturesStore
                .isDeviceAssigned(`zigbee-${action.payload.id.toLowerCase()}`)
                .pipe(map((response) => response.assigned)),
              this.gatewayStore.isRemoteDeviceCommissioned(
                `zigbee-${action.payload.id.toLowerCase()}`
              )
            ).pipe(
              map((isDeviceAssignedResponse) =>
                CommissioningActions.updateFeaturePropertySuccess({
                  isAlreadyAdded: isDeviceAssignedResponse,
                  deviceId: `zigbee-${action.payload.id.toLowerCase()}`,
                  config: action.config,
                })
              )
            )
          ),
          catchError(() => {
            this.uiService.unblockUI();
            this.updateMessageService.error(
              action.config.errorMessage,
              action.featureName,
              action.commandName
            );

            return of(
              CommissioningActions.updateFeaturePropertyError({
                featureName: action.featureName,
                property: action.propertyName,
              })
            );
          })
        )
      )
    );
  });

  verifyEuid$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(CommissioningActions.verifyEuid.attempt),
      concatLatestFrom(() => [
        this.commissioningStore.currentCode$,
        this.commissioningStore.isAddingDirectlyToRoom$,
      ]),
      switchMap(([_action, currentCode, isAddingDirectlyToRoom]) => {
        // If user tries to add a device directly to a room verify if the device is assigned anywhere already.
        // If user tries to add a device generally verify if it is already added.
        return iif(
          () => isAddingDirectlyToRoom,
          this.installationFeaturesStore
            .isDeviceAssigned(`zigbee-${currentCode.id.toLowerCase()}`)
            .pipe(map((response) => response.assigned)),
          this.gatewayStore.isRemoteDeviceCommissioned(`zigbee-${currentCode.id.toLowerCase()}`)
        ).pipe(
          map((isDeviceAssignedResponse) => {
            if (isDeviceAssignedResponse) {
              return CommissioningActions.verifyEuid.error({
                error: ValidateEuidResult.ErrorAlreadyAdded,
              });
            } else {
              return CommissioningActions.verifyEuid.success();
            }
          })
        );
      })
    );
  });

  updateRemoteDeviceFeaturePropertySuccess$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(CommissioningActions.updateFeaturePropertySuccess),
      filter((action) => action?.config?.refresh),
      concatMap((action) =>
        of('').pipe(
          delay(action?.config?.refreshDelay || 0),
          map(() => CommissioningActions.listFeatures())
        )
      )
    );
  });

  triggerPairing$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(CommissioningActions.setActiveStep),
      filter((action) => action.step === COMMISSIONING_STEP.PAIRING),
      concatLatestFrom(() => [
        this.commissioningStore.currentCode$,
        this.commissioningStore.deviceType$,
      ]),
      map(([_action, code, type]) => {
        return CommissioningActions.updateFeatureProperty({
          featureName: FeatureNames.deviceZigbeeCoordinator,
          value: [code.ic, code.id, AddDeviceType[type]],
          propertyName: null,
          paramName: ['ic', 'id', 'type'],
          commandName: FeatureCommands.addDevice,
          config: {
            actionName: 'addDevice',
            errorMessage: this.translateService.instant(
              '+COMMISSIONING.STATUS.COULD_NOT_IDENTIFY_DEVICE'
            ),
            hideSuccessSnackbar: true,
          },
        });
      })
    );
  });

  addDeviceSuccess$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(CommissioningActions.updateFeaturePropertySuccess),
      concatLatestFrom(() => this.commissioningStore.activeStep$),
      filter(
        ([action, step]) =>
          action?.config?.actionName === 'addDevice' &&
          (step === COMMISSIONING_STEP.IDENTIFY_CODE || step === COMMISSIONING_STEP.TYPE_CODE)
      ),
      map(() => {
        return CommissioningActions.setActiveStep({
          step: COMMISSIONING_STEP.IDENTIFY_CODE_SUCCESS,
        });
      })
    );
  });

  updateCoordinatorStatus$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(CommissioningActions.updatePairingStatus),
      switchMap(() =>
        timer(3000, 4000).pipe(
          takeUntil(
            merge(
              this.actions$.pipe(ofType(CommissioningActions.Type.UPDATE_PAIRING_STATUS_ERROR)),
              this.actions$.pipe(ofType(CommissioningActions.Type.UPDATE_PAIRING_STATUS_SUCCESS)),

              // stop if user goes to next step (e.g. clicks on back button)
              this.actions$.pipe(ofType(CommissioningActions.Type.SET_ACTIVE_STEP)),

              // stop if user goes to other page (browser back button or breadcrumb back button)
              this.actions$.pipe(ofType(CommissioningActions.Type.INIT_COMMISSIONING))
            )
          ),
          mergeMap((timeCount) =>
            combineLatest([
              of(timeCount),
              this.installationDetailsStore.getInstallationId$.pipe(take(1)),
              this.installationDetailsStore.gatewaySerial$.pipe(take(1)),
            ])
          ),
          tap(([timeCount, installationId, gatewaySerial]) => {
            // Timeout error will be thrown after 120 seconds
            if (timeCount * 4000 > 120000) {
              this.commissioningMessageService.toast(ZIGBEE_COORDINATOR_STATUS.TIMEOUT);
              this.router.navigate([`/installations/${installationId}/commissioning`], {
                queryParams: { gatewaySerial },
              });
              this.store.dispatch(
                CommissioningActions.updatePairingStatusError({
                  step: COMMISSIONING_STEP.PAIRING_FAILED,
                })
              );
            }
          }),
          switchMap(([_timeCount, installationId, gatewaySerial]) =>
            combineLatest([
              of(installationId),
              of(gatewaySerial),
              this.api
                .getGatewayFeature(installationId, gatewaySerial, FeatureNames.gatewayDevices)
                .pipe(map(extractDevices)),
            ])
          ),
          map(
            ([installationId, gatewaySerial, devices]) =>
              [installationId, gatewaySerial, devices, getCoordinatorDeviceId(devices)] as [
                number,
                string,
                GatewayDevice[],
                string
              ]
          ),
          switchMap(([installationId, gatewaySerial, devices, coordinatorDeviceId]) =>
            this.api
              .getDeviceFeature(
                installationId,
                gatewaySerial,
                coordinatorDeviceId,
                FeatureNames.deviceZigbeeCoordinator
              )
              .pipe(
                concatLatestFrom(() => [
                  this.commissioningStore.currentCode$,
                  this.commissioningStore.pairingStatus$,
                  this.commissioningStore.deviceType$,
                ]),
                map(([payload, code, pairingStatus, deviceType]) => ({
                  status: extractStatus(
                    payload as {
                      data: Feature<DeviceZigbeeCoordinatorProperties, ZigbeeCoordinatorCommands>;
                    },
                    code.id
                  ),
                  devices,
                  code,
                  pairingStatus,
                  deviceType,
                })),
                filter(
                  (payload) =>
                    // Zigbee coordinator can return OK after adding or removing action, so we have
                    // to filter changes other then add to do not get false success of pairing
                    payload.status !== ZIGBEE_COORDINATOR_STATUS.OK ||
                    payload.pairingStatus.status === ZIGBEE_COORDINATOR_STATUS.LEARNING ||
                    payload.pairingStatus.status === ZIGBEE_COORDINATOR_STATUS.ALREADY_PAIRED
                ),
                map((payload) => {
                  this.viLogger.log(
                    `zigbee.coordinator.properties.status.value: ${payload.status}`,
                    LogType.info
                  );

                  const deviceId = `zigbee-${payload.code.id.toLocaleLowerCase()}`;

                  switch (payload.status) {
                    case ZIGBEE_COORDINATOR_STATUS.INVALID_FORMAT:
                      this.commissioningMessageService.toast(
                        ZIGBEE_COORDINATOR_STATUS.INVALID_FORMAT
                      );

                      return CommissioningActions.updatePairingStatusError({
                        step: COMMISSIONING_STEP.IDENTIFY_CODE,
                      });

                    case ZIGBEE_COORDINATOR_STATUS.ALREADY_PAIRED:
                      const deviceAlreadyAdded = payload.devices.find(
                        (device: GatewayDevice) => device.id.toLocaleLowerCase() === deviceId
                      );

                      if (deviceAlreadyAdded) {
                        this.commissioningMessageService.toast(
                          ZIGBEE_COORDINATOR_STATUS.ALREADY_PAIRED
                        );

                        return CommissioningActions.updatePairingStatusSuccess({
                          coordinatorStatus: payload.status,
                        });
                      }

                      /**
                       * if device is not found on the list we wait for next status from zigbee coordinator
                       */
                      return CommissioningActions.setPairingStatus({
                        status: ZIGBEE_COORDINATOR_STATUS.LEARNING,
                        step: PAIRING_STEPS.LEARNING,
                      });

                    case ZIGBEE_COORDINATOR_STATUS.TIMEOUT:
                      this.commissioningMessageService.toast(ZIGBEE_COORDINATOR_STATUS.TIMEOUT);
                      this.router.navigate([`/installations/${installationId}/commissioning`], {
                        queryParams: { gatewaySerial },
                      });

                      return CommissioningActions.updatePairingStatusError({
                        step: COMMISSIONING_STEP.PAIRING_FAILED,
                      });

                    case ZIGBEE_COORDINATOR_STATUS.INITIATING:
                      return CommissioningActions.setPairingStatus({
                        status: ZIGBEE_COORDINATOR_STATUS.INITIATING,
                        step: PAIRING_STEPS.INITIATING,
                      });

                    case ZIGBEE_COORDINATOR_STATUS.LEARNING:
                      return CommissioningActions.setPairingStatus({
                        status: ZIGBEE_COORDINATOR_STATUS.LEARNING,
                        step: PAIRING_STEPS.LEARNING,
                      });

                    case ZIGBEE_COORDINATOR_STATUS.LEARNING_FAILED:
                      this.commissioningMessageService.toast(
                        ZIGBEE_COORDINATOR_STATUS.LEARNING_FAILED
                      );

                      return CommissioningActions.updatePairingStatusError({
                        step: COMMISSIONING_STEP.PAIRING_FAILED,
                      });

                    case ZIGBEE_COORDINATOR_STATUS.OK: {
                      return CommissioningActions.setConnectingPairingStatus({
                        status: ZIGBEE_COORDINATOR_STATUS.CONNECTING,
                        step: PAIRING_STEPS.CONNECTING,
                        deviceId: deviceId,
                        installationId: installationId,
                        gatewaySerial: gatewaySerial,
                      });
                    }

                    case ZIGBEE_COORDINATOR_STATUS.TYPE_MISMATCH:
                      return CommissioningActions.updatePairingStatusError({
                        step: COMMISSIONING_STEP.PAIRING_FAILED,
                        status: ZIGBEE_COORDINATOR_STATUS.TYPE_MISMATCH,
                      });

                    case ZIGBEE_COORDINATOR_STATUS.ZIGBEE_ERROR:
                      this.commissioningMessageService.toast(
                        ZIGBEE_COORDINATOR_STATUS.ZIGBEE_ERROR
                      );
                      if (payload.deviceType === RemoteDeviceType.VITOTROL_300E) {
                        return CommissioningActions.updatePairingStatusError({
                          step: COMMISSIONING_STEP.PAIRING_ENDED,
                        });
                      } else {
                        return CommissioningActions.updatePairingStatusError({
                          step: COMMISSIONING_STEP.PAIRING_FAILED,
                        });
                      }

                    default:
                      throw new Error('Pairing has not began or timeouted.');
                  }
                })
              )
          ),
          retry(2),
          catchError(() => {
            this.commissioningMessageService.toast(ZIGBEE_COORDINATOR_STATUS.UNKNOWN);

            return of(
              CommissioningActions.updatePairingStatusError({
                step: COMMISSIONING_STEP.PAIRING_FAILED,
              })
            );
          })
        )
      )
    );
  });

  onDevicePaired$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(CommissioningActions.updatePairingStatusSuccess),
      filter((action) => {
        return action.coordinatorStatus === ZIGBEE_COORDINATOR_STATUS.OK;
      }),
      concatLatestFrom(() => [this.installationDetailsStore.getInstallationId$]),
      switchMap(([_action, installationId]) => {
        return of(InstallationDetailsActions.ReadInstallationDetails({ installationId }));
      })
    );
  });

  setConnectingPairingStatus$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(CommissioningActions.setConnectingPairingStatus),
      // Artificial delay to mock ViCare App behavior
      delay(3000),
      mergeMap((action) =>
        this.api
          .getGatewayFeature(
            action.installationId,
            action.gatewaySerial,
            FeatureNames.gatewayDevices
          )
          .pipe(
            map(extractDevices),
            map((devices) => {
              return devices.find((device) => device.id === action.deviceId);
            }),
            // Repeat request if device status is offline using 5s interval, at 5th attempt throw an error
            repeatWhen((result) => result.pipe(delay(5000))),
            concatMap((response, requestIndex) => {
              if (requestIndex === 5) {
                throw new Error('Device offline');
              } else {
                return of(response);
              }
            }),
            filter((dev: GatewayDevice) => dev.status === DEVICE_STATUS.ONLINE),
            take(1),
            map(() => {
              return CommissioningActions.updatePairingStatusSuccess({
                coordinatorStatus: ZIGBEE_COORDINATOR_STATUS.OK,
              });
            }),
            catchError(() => {
              return of(
                CommissioningActions.updatePairingStatusError({
                  step: COMMISSIONING_STEP.PAIRING_FAILED,
                })
              );
            })
          )
      )
    );
  });

  deviceAssignAttempt$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(CommissioningActions.deviceAssign.attempt),
      concatLatestFrom(() => this.commissioningStore.deviceAssignmentParams$),
      switchMap(([_action, deviceAssignmentParams]) => {
        return merge(
          of({ type: 'hc', params: deviceAssignmentParams.heatingCircuit }).pipe(
            filter((config) => !!config.params)
          ),
          of({ type: 'rmHc', params: deviceAssignmentParams.rmHeatingCircuit }).pipe(
            filter((config) => !!config.params)
          ),
          of({ type: 'ventilation', params: deviceAssignmentParams.ventilation }).pipe(
            filter((config) => !!config.params)
          ),
          of({ type: 'room', params: deviceAssignmentParams.room }).pipe(
            filter((config) => !!config.params)
          ),
          from(deviceAssignmentParams.channels).pipe(
            filter((config) => !!config),
            map((entry) => {
              return {
                type: 'channels',
                params: entry,
              };
            })
          )
        ).pipe(
          concatMap((config) => {
            switch (config.type) {
              case 'hc': {
                const stream$ = this.actions$.pipe(
                  ofType(InstallationFeaturesActions.updateFeature.success),
                  take(1),
                  timeout(10000),
                  map(() => {
                    return CommissioningActions.deviceAssign.sequenceSuccess({
                      data: config.params,
                    });
                  }),
                  catchError((error) =>
                    of(
                      CommissioningActions.deviceAssign.sequenceError({
                        error,
                        data: config.params,
                      })
                    )
                  )
                );

                const {
                  circuitId,
                  deviceId,
                } = config.params as CommissioningDeviceAssignHeatingCircuitParams;
                this.assingDeviceToHeatingCircuit(deviceId, circuitId);

                return stream$.pipe(delay(5000));
              }
              case 'rmHc': {
                const stream$ = this.actions$.pipe(
                  ofType(InstallationFeaturesActions.updateFeature.success),
                  take(1),
                  timeout(10000),
                  map(() => {
                    return CommissioningActions.deviceAssign.sequenceSuccess({
                      data: config.params,
                    });
                  }),
                  catchError((error) =>
                    of(
                      CommissioningActions.deviceAssign.sequenceError({
                        error,
                        data: config.params,
                      })
                    )
                  )
                );

                const {
                  circuitId,
                  deviceId,
                } = (config.params as unknown) as CommissioningRemoteDeviceAssignHeatingCircuitParams;
                this.assingRemoteDeviceToHeatingCircuit(deviceId, circuitId);

                return stream$.pipe(delay(5000));
              }
              case 'ventilation': {
                const stream$ = this.actions$.pipe(
                  ofType(InstallationFeaturesActions.updateFeature.success),
                  take(1),
                  timeout(10000),
                  map(() => {
                    return CommissioningActions.deviceAssign.sequenceSuccess({
                      data: config.params,
                    });
                  }),
                  catchError((error) =>
                    of(
                      CommissioningActions.deviceAssign.sequenceError({
                        error,
                        data: config.params,
                      })
                    )
                  )
                );

                const {
                  deviceId,
                } = (config.params as unknown) as CommissioningRemoteDeviceAssignVentilationParams;
                this.assignToVentilation(deviceId);

                return stream$.pipe(delay(5000));
              }
              case 'room': {
                const stream$ = this.actions$.pipe(
                  ofType(InstallationFeaturesActions.updateFeature.success),
                  take(1),
                  timeout(10000),
                  map(() => {
                    return CommissioningActions.deviceAssign.sequenceSuccess({
                      data: config.params,
                    });
                  }),
                  catchError((error) =>
                    of(
                      CommissioningActions.deviceAssign.sequenceError({
                        error,
                        data: config.params,
                      })
                    )
                  )
                );

                const {
                  deviceId,
                  roomFeature,
                  heatingCircuitId,
                } = config.params as CommissioningDeviceAssignRoomParams;
                this.assignDeviceToRoom(deviceId, roomFeature, heatingCircuitId);

                return stream$;
              }
              case 'channels': {
                const stream$ = this.actions$.pipe(
                  ofType(InstallationFeaturesActions.updateFeature.success),
                  take(1),
                  timeout(10000),
                  map(() => {
                    return CommissioningActions.deviceAssign.sequenceSuccess({
                      data: config.params,
                    });
                  }),
                  catchError((error) =>
                    of(
                      CommissioningActions.deviceAssign.sequenceError({
                        error,
                        data: config.params,
                      })
                    )
                  )
                );

                const {
                  fhtChannelDeviceId,
                  roomFeature,
                } = config.params as CommissioningDeviceAssignChannelParams;
                this.assignFhtChannelToRoom(fhtChannelDeviceId, roomFeature);

                return stream$.pipe(delay(2000));
              }
            }
          }),
          toArray()
        );
      }),
      switchMap((allActionsResult) => {
        const isError = allActionsResult.some((entry) =>
          entry.type.toLowerCase().includes('error')
        );

        const validationResult = allActionsResult.map((entry) => {
          return {
            ...(entry as any).data,
            isSuccess: !entry.type.toLowerCase().includes('error'),
          } as CommissioningDeviceAssignValidationParams;
        });

        if (isError) {
          return of(CommissioningActions.deviceAssign.error({ validationData: validationResult }));
        } else {
          return of(
            CommissioningActions.deviceAssign.success({ validationData: validationResult })
          );
        }
      })
    );
  });

  constructor(
    private api: OperationalDataApi,
    private client: ClientService,
    private uiService: UIService,
    private updateMessageService: UpdateMessageService,
    private commissioningMessageService: CommissioningMessageService,
    private actions$: Actions,
    private store: Store<AppState>,
    private installationDetailsStore: InstallationDetailsStore,
    private router: Router,
    private viLogger: ViLogger,
    private commissioningStore: CommissioningStore,
    private gatewayStore: GatewayStore,
    private translateService: TranslateService,
    private installationFeaturesStore: InstallationFeaturesStore
  ) {}

  assignDeviceToRoom(deviceId: string, roomFeature: string, heatingCircuitId?: number): void {
    this.installationFeaturesStore.updateFeature(
      roomFeature,
      [deviceId, heatingCircuitId],
      null,
      [FeaturePropertyKey.actorDeviceId, FeaturePropertyKey.heatingCircuit],
      FeatureCommands.addActor,
      null,
      DeviceIds.RoomControl
    );
  }

  assignFhtChannelToRoom(fhtChannelDeviceId: string, roomFeature: string): void {
    zip(
      this.installationFeaturesStore.selectInstallationDeviceFeature<
        IsNumberValueProperty,
        DeviceHeatingCircuitIdCommands
      >(FeatureNames.deviceHeatingCircuitId, fhtChannelDeviceId),
      this.installationFeaturesStore.selectInstallationDeviceFeature<IsEnabledProperty, {}>(
        FeatureNames.circuits
      )
    )
      .pipe(take(1))
      .subscribe(([_deviceHeatingCircuit, _heatingCircuit]) => {
        const deviceHeatingCircuit = _deviceHeatingCircuit?.properties?.value?.value;
        const firstEnabledHC = _heatingCircuit?.properties?.enabled?.value?.[0] || 0;

        // E3 device expects a heating circuit when assigning device to a room
        // when hc is missing we assign a default first enabled hc
        // for devices that don't have HC value is ignored
        const hcToAssign =
          deviceHeatingCircuit != null ? deviceHeatingCircuit : Number(firstEnabledHC);

        this.installationFeaturesStore.updateFeature(
          roomFeature,
          [fhtChannelDeviceId, hcToAssign],
          null,
          [FeaturePropertyKey.actorDeviceId, FeaturePropertyKey.heatingCircuit],
          FeatureCommands.addActor,
          null,
          DeviceIds.RoomControl
        );
      });
  }

  assingDeviceToHeatingCircuit(deviceId: string, circuitId: string): void {
    this.installationFeaturesStore.updateFeature(
      FeatureNames.deviceHeatingCircuitId,
      Number(circuitId),
      null,
      FeaturePropertyKey.value,
      FeatureCommands.assignHeatingCircuit,
      null,
      deviceId
    );
  }

  private assingRemoteDeviceToHeatingCircuit(deviceId: string, circuitId: string): void {
    this.installationFeaturesStore.updateFeature(
      FeatureNames.heatingCircuitRemoteController(circuitId),
      deviceId,
      FeaturePropertyKey.deviceId,
      FeatureActionFields.deviceId,
      FeatureCommands.setZigbeeController
    );
  }
  private assignToVentilation(deviceId): void {
    this.installationFeaturesStore.updateFeature(
      FeatureNames.ventilationRemoteController,
      deviceId,
      FeaturePropertyKey.deviceId,
      FeatureActionFields.deviceId,
      FeatureCommands.setZigbeeController
    );
  }
}

const extractDevices = (payload: {
  data: FeatureDeprecated<{ devices: ArrayValue<GatewayDevice> }>;
}): GatewayDevice[] => {
  return payload.data.properties?.devices?.value || [];
};

const extractStatus = (
  payload: {
    data: Feature<DeviceZigbeeCoordinatorProperties, ZigbeeCoordinatorCommands>;
  },
  id: string
): string => {
  const zigbeeCoordinatorStatusArray = payload.data.properties?.status?.value || [];
  const deviceStatus = zigbeeCoordinatorStatusArray.find(
    (device) => device.device && device.device.toUpperCase() === `zigbee-${id}`.toUpperCase()
  );

  return deviceStatus?.value;
};

const getCoordinatorDeviceId = (gatewayDevices: GatewayDevice[]): string => {
  const coordinatorDevice = gatewayDevices.find((device) =>
    device.roles.includes(DeviceRoles.ZigbeeCoordinator)
  );

  if (!coordinatorDevice) {
    throw new Error('No device with coordinator capability available.');
  }

  return coordinatorDevice.id;
};
